import { ReactNode } from "react";
import { alpha, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { RootCauseWidgetWaterfallDTO } from "../../../../datasources/api-client/data-contracts";
import { useCurrentTheme } from "../../../../theme/theme";
import CustomLegend from "./CustomLegend";
import CustomTooltip from "./CustomTooltip";
import { computeWaterfallData, WaterfallKey } from "./data";

interface Props {
	data: RootCauseWidgetWaterfallDTO | undefined;
	labels: Record<WaterfallKey, string>;
	noDataOverlay: ReactNode;
}

export const WaterfallChart = ({ data, labels, noDataOverlay }: Props) => {
	const { theme } = useCurrentTheme();
	const { t } = useTranslation("common");

	const waterfallData = computeWaterfallData(data, theme, labels);

	const isEmpty = waterfallData.length === 0;

	const chartData = isEmpty
		? [
				{
					key: "empty",
					name: "",
					value: 0,
					measure: "",
					color: "transparent",
					type: "increase" as const,
					waterfallRange: [0, 0] as [number, number],
				},
			]
		: waterfallData;

	const barSize = isEmpty ? 0 : `${Math.max(10, Math.round(80 / waterfallData.length))}%`;

	const legendPayload = [
		{
			label: labels.baseline,
			color: theme.palette.info.light,
		},
		{
			label: t("increase"),
			color: theme.palette.error.light,
		},
		{
			label: t("decrease"),
			color: theme.palette.success.light,
		},
		{
			label: labels.comparison,
			color: theme.palette.info.main,
		},
	];

	return (
		<Stack width="100%" height={400} position="relative">
			{isEmpty && (
				<Stack alignItems="center" justifyContent="center" zIndex={1}>
					{noDataOverlay}
				</Stack>
			)}

			<ResponsiveContainer width="100%" height={350}>
				<BarChart
					data={chartData}
					margin={{
						top: 8,
						right: 16,
						left: 16,
						bottom: 8,
					}}
				>
					<XAxis
						dataKey="name"
						interval={0}
						axisLine
						tickLine
						tick={{
							dy: 4,
							fontSize: 9,
							fontWeight: 500,
						}}
					/>

					<YAxis
						domain={[(dataMin: number) => Math.min(0, dataMin), (dataMax: number) => Math.max(0, dataMax)]}
						allowDataOverflow={false}
						axisLine
						tickLine
						tick={{
							fontSize: 9,
							fontWeight: 500,
						}}
						tickFormatter={(value: number) => new Intl.NumberFormat("it-IT").format(value)}
					/>

					{!isEmpty && (
						<Bar dataKey="waterfallRange" barSize={barSize} isAnimationActive={false} activeBar={false}>
							{waterfallData.map((item) => (
								<Cell key={item.key} fill={item.color} />
							))}
						</Bar>
					)}

					{!isEmpty && (
						<Tooltip
							cursor={{
								fill: alpha(theme.palette.info.main, 0.06),
							}}
							content={(props) => <CustomTooltip {...props} />}
						/>
					)}

					<Legend verticalAlign="bottom" align="center" content={<CustomLegend data={legendPayload} />} />
				</BarChart>
			</ResponsiveContainer>
		</Stack>
	);
};
